import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBatteryFull } from '@fortawesome/free-solid-svg-icons/faBatteryFull'
import { faBatteryThreeQuarters } from '@fortawesome/free-solid-svg-icons/faBatteryThreeQuarters'
import { faBatteryHalf } from '@fortawesome/free-solid-svg-icons/faBatteryHalf'
import { faBatteryQuarter } from '@fortawesome/free-solid-svg-icons/faBatteryQuarter'
import { faBatteryEmpty } from '@fortawesome/free-solid-svg-icons/faBatteryEmpty'
import { faBolt } from '@fortawesome/free-solid-svg-icons/faBolt'
import dataStore from '../../../store/dataStore.ts'
import { IPet } from '../../../interfaces/types.ts'
import { SubItem } from './SubItem.tsx'

interface Props {
    pet: IPet
    mini?: boolean
    mobile?: boolean
    dashboard?: boolean
    iconOnly?: boolean
}

type BatteryColor = 'text-gray-400' | 'text-error' | 'text-amber-500' | 'text-success' | 'text-info'

const getBatteryIcon = (on: boolean, _charging: boolean, beacon: boolean, percent: number) => {
    if (!on) return faBatteryEmpty
    if (beacon) return faBatteryFull // power saving shows full (device is conserving)
    if (percent >= 75) return faBatteryFull
    if (percent >= 50) return faBatteryThreeQuarters
    if (percent >= 25) return faBatteryHalf
    if (percent > 0) return faBatteryQuarter
    return faBatteryEmpty
}

const getBatteryColor = (on: boolean, charging: boolean, percent: number): BatteryColor => {
    if (!on) return 'text-gray-400'
    if (charging) return 'text-info'
    if (percent >= 50) return 'text-success'
    if (percent >= 20) return 'text-amber-500'
    return 'text-error'
}

// Derive all display values from device state. Pure function.
const deriveBatteryDisplay = (powerOn: boolean, charging: boolean, batteryAlert: number, batteryPercent: string, beaconInRange: boolean) => {
    const isOff = !powerOn
    let status: 'off' | 'charging' | 'low' | 'on'

    if (isOff) {
        status = 'off'
    } else if (charging) {
        status = 'charging'
    } else if (batteryAlert) {
        status = 'low'
    } else {
        status = 'on'
    }

    const numPercent = isOff ? 0 : (parseFloat(batteryPercent) || 0)
    const displayPercent = isOff ? 'off' : (charging && numPercent < 100) ? '' : batteryPercent
    const colorClass = getBatteryColor(powerOn, charging, numPercent)
    const icon = getBatteryIcon(powerOn, charging, beaconInRange, numPercent)

    return { isOff, status, colorClass, displayPercent, icon, numPercent }
}

export const Battery = (props: Props) => {
    const { pet, mobile, iconOnly } = props
    const devices = dataStore((s) => s.devices)
    const device = devices.find(d => d.petId === pet._id) ?? null

    if (!device) return null

    const { powerOn, charging, beaconInRange, batteryAlert, batteryPercent } = device.status
    const { isOff, status, colorClass, displayPercent, icon } = deriveBatteryDisplay(
        powerOn, charging, batteryAlert, batteryPercent, beaconInRange
    )

    const size = mobile ? 'lg' : 'xl'
    const containerStyle = mobile
        ? { height: '33.5px', width: '33.5px', border: '2.25px white solid' }
        : { height: '40px', width: '40px', border: '2px white solid' }

    const batteryIconEl = (
        <div className={`rounded-full bg-white flex items-center justify-center ${charging ? 'text-gray-400' : colorClass} -rotate-90`} style={containerStyle}>
            {charging ? (
                <span className="fa-layers fa-fw">
                    <FontAwesomeIcon icon={faBatteryEmpty} size={size} style={{position:"absolute", left: "-2px"}}/>
                    <FontAwesomeIcon icon={faBolt} size={'xs'} className="text-amber-500 rotate-90" />
                </span>
            ) : (
                <FontAwesomeIcon icon={icon} size={size} />
            )}
        </div>
    )

    const subItem = (
        <SubItem pet={pet} title={'Battery'} icon={batteryIconEl} className={'-ml-1 -mr-2'} mobile={mobile}>
            <div className={'flex items-center justify-between'}>
                <div className={`${colorClass} font-md text-lg`}>
                    {!isOff
                        ? <span> Battery at <span className={'font-semibold'}>{displayPercent}</span></span>
                        : <span className={'font-semibold'}>Off</span>}
                </div>
                <div className={`text-md ${colorClass}`}>
                    Tracker is {status}
                </div>
            </div>
        </SubItem>
    )

    const batteryIconOnly = (
        <div className={'grid grid-rows-2 gap-1'}>
            <div className={'justify-self-center'}>{batteryIconEl}</div>
            <div className={'bg-transparent text-primary-content justify-self-center'}>{displayPercent}</div>
        </div>
    )

    return iconOnly ? batteryIconOnly : subItem
}
